import { useState } from 'react';
import { dePersonne } from '../messages/format';
import type { ParticipantPassation } from '../../types';
import { autres_participants, gesteCorrection, quePrenom, qui_debute, retirees, signauxAffiches, trierParticipants, type ResumeReprise } from './accueil';
import { TexteAdaptatif } from './TexteAdaptatif';

/** Qui parle à qui : « vous » pour l'intervenant·e absent·e, son prénom pour la gestion et pour la remplaçante (jamais « la personne »). */
export interface Voix { vous: boolean; prenomAbsent: string; prenomRemplacant: string }

const Titre = ({ id, numero, children, compteur }: { id: string; numero?: number; children: string; compteur?: string }) => (
  <h3 className="card-title" id={id}>
    {numero != null && <span className="pg-num" aria-hidden="true">{numero}</span>}
    {children}
    {compteur && <span className="pg-compteur" id={`${id}-compteur`}>{compteur}</span>}
  </h3>
);

/** 1 · Qui débute ? — la liste de celles et ceux à accueillir en particulier, et de quoi la corriger (l'intervenant·e connaît son groupe). */
export function SectionQuiDebute({ participants, voix, ecrire, numero, onCorriger }: {
  participants: ParticipantPassation[]; voix: Voix; ecrire: boolean; numero?: number;
  onCorriger: (participantId: number, valeur: boolean | null) => void;
}) {
  const [choix, setChoix] = useState('');
  const liste = qui_debute(participants);
  const autres = autres_participants(participants);
  const sorties = retirees(participants);
  const avecDoute = liste.some((p) => p.signaux.some((s) => s.startsWith('peut-être')));
  const aide = ecrire
    ? (voix.vous ? 'Vous connaissez le groupe : corrigez si Eveil se trompe.' : `${voix.prenomAbsent} connaît le groupe : corrigez seulement si Eveil se trompe.`)
    : `${voix.prenomAbsent} a pu corriger la liste : ${voix.prenomAbsent} connaît le groupe.`;

  return (
    <section className="card pg-section" id="pg-debute" tabIndex={-1} aria-labelledby="pg-debute-t">
      <Titre id="pg-debute-t" numero={numero}>Qui débute ?</Titre>
      <p className="pg-aide" style={{ margin: '0 0 var(--s8)' }}>
        D’après les présences saisies dans Eveil.{avecDoute && ' « Peut-être » : ses présences ne sont pas toutes saisies.'} {aide}
      </p>
      {liste.length === 0 ? (
        <p className="pg-vide" style={{ margin: 'var(--s8) 0' }}>Personne ne débute, d’après les présences saisies dans Eveil.</p>
      ) : (
        <ul className="pg-debute">
          {liste.map((p) => {
            const geste = ecrire ? gesteCorrection(p) : null;
            return (
              <li key={p.participant_id} className="pg-debute-ligne">
                <b>{p.nom}</b>
                {signauxAffiches(p, voix.prenomAbsent).map((sg) => (
                  <span key={sg.texte} className={`pg-puce${sg.doute ? ' doute' : ''}`}>
                    {sg.texte}{sg.indiquePar && <small>· indiqué par {sg.indiquePar}</small>}
                  </span>
                ))}
                {geste && (
                  <button type="button" className="btn btn-sm pg-geste" data-geste={p.participant_id} aria-label={geste.aria} onClick={() => onCorriger(p.participant_id, geste.valeur)}>
                    {geste.libelle}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {ecrire && autres.length > 0 && (
        <div className="pg-ajout">
          <label htmlFor="pg-ajout-choix">Quelqu’un d’autre débute dans l’atelier ?</label>
          <select id="pg-ajout-choix" className="form-select" style={{ width: 'auto', maxWidth: '100%' }} value={choix} onChange={(e) => setChoix(e.target.value)}>
            <option value="">Choisir une personne…</option>
            {autres.map((p) => <option key={p.participant_id} value={p.participant_id}>{p.nom}</option>)}
          </select>
          <button type="button" className="btn btn-sm" disabled={choix === ''} onClick={() => { onCorriger(Number(choix), true); setChoix(''); }}>Ajouter à la liste</button>
        </div>
      )}
      {ecrire && sorties.length > 0 && (
        <div className="pg-ajout">
          Retiré de la liste :
          {sorties.map((p) => (
            <span key={p.participant_id} className="pg-retiree">
              {p.nom}
              <button type="button" className="btn btn-sm btn-ghost" data-retiree={p.participant_id} aria-label={`Annuler le retrait ${dePersonne(p.nom).replace("'", '’')}`} onClick={() => onCorriger(p.participant_id, null)}>Annuler</button>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

/** 2 · Un mot pour chacun·e — ce qui aide à bien accueillir ; « Rien de particulier » suffit souvent (et n'écrase jamais un mot déjà écrit). */
export function SectionMots({ participants, remarques, voix, ecrire, lireTextes, numero, repris, onChange, onBlur, onRien }: {
  participants: ParticipantPassation[]; remarques: Record<number, string>; voix: Voix; ecrire: boolean; lireTextes: boolean; numero?: number;
  /** Ce qu'une reprise a mis dans les champs : tant que le texte n'a pas été retravaillé, la ligne dit « Repris du … · à relire ». */
  repris: ResumeReprise | null;
  onChange: (participantId: number, texte: string) => void; onBlur: () => void; onRien: (participantId: number) => void;
}) {
  const triees = trierParticipants(participants);
  const faits = participants.filter((p) => (remarques[p.participant_id] ?? '').trim() !== '').length;
  return (
    <section className="card pg-section" id="pg-mots" tabIndex={-1} aria-labelledby="pg-mots-t">
      <Titre id="pg-mots-t" numero={numero} compteur={ecrire ? `${faits} sur ${participants.length}` : undefined}>Un mot pour chacun·e</Titre>
      {!lireTextes ? (
        <div className="pg-attente">Apparaîtra ici dès {quePrenom(voix.prenomAbsent)} aura transmis sa passation.</div>
      ) : (
        <>
          {ecrire && <p className="pg-aide" style={{ margin: '0 0 var(--s8)' }}>Ce qui aide à bien l’accueillir, à {voix.vous ? 'votre' : 'sa'} discrétion. Rien de médical ni de confidentiel. « Rien de particulier » suffit souvent.</p>}
          <ul className="pg-mots">
            {triees.map((p) => {
              const debute = p.signaux.length > 0;
              const texte = remarques[p.participant_id] ?? '';
              const reprisIci = repris?.remarques.some((x) => x.participant_id === p.participant_id && x.remarque === texte);
              return (
                <li key={p.participant_id} className="pg-mot" style={ecrire ? undefined : { gridTemplateColumns: '180px minmax(0, 1fr)' }}>
                  <div className="pg-mot-nom">{p.nom}{debute && <span className="pg-tag">Débute</span>}</div>
                  {ecrire ? (
                    <>
                      <div>
                        <TexteAdaptatif id={`passation-remarque-${p.participant_id}`} etiquette={`Un mot pour accueillir ${p.nom}`} valeur={texte} minLignes={1} maxLength={1000}
                          placeholder={debute ? 'Ce qui l’aidera à se sentir à l’aise' : 'Ce qui aide à l’accueillir'} classe={`pg-champ${texte.trim() ? ' pg-rempli' : ''}`}
                          onChange={(v) => onChange(p.participant_id, v)} onBlur={onBlur} />
                        {reprisIci && repris && <div className="pg-aide">Repris du {repris.date} · à relire</div>}
                      </div>
                      {texte.trim() === '' ? (
                        <button type="button" className="btn btn-sm pg-ras" aria-label={`Rien de particulier pour ${p.nom}`} onClick={() => onRien(p.participant_id)}>Rien de particulier</button>
                      ) : <span />}
                    </>
                  ) : (
                    <div className={`pg-texte pg-lecture${texte.trim() ? '' : ' pg-vide'}`}>{texte.trim() || 'Rien d’indiqué'}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}

/** 3 · Côté pratique — lieu, clés, matériel, déroulé ; avec la reprise de la dernière passation du même atelier, en une ligne. */
export function SectionPratique({ logistique, voix, ecrire, lireTextes, numero, offreReprise, repriseFaite, onChange, onBlur, onReprendre, onAnnulerReprise }: {
  logistique: string; voix: Voix; ecrire: boolean; lireTextes: boolean; numero?: number;
  offreReprise: { date: string; auteur: string } | null; repriseFaite: ResumeReprise | null;
  onChange: (texte: string) => void; onBlur: () => void; onReprendre: () => void; onAnnulerReprise: () => void;
}) {
  return (
    <section className="card pg-section" id="pg-pratique" tabIndex={-1} aria-labelledby="pg-pratique-t">
      <Titre id="pg-pratique-t" numero={numero}>Côté pratique</Titre>
      {!lireTextes ? (
        <div className="pg-attente">Apparaîtra ici dès {quePrenom(voix.prenomAbsent)} aura transmis sa passation.</div>
      ) : ecrire ? (
        <>
          {offreReprise && !repriseFaite && (
            <div className="pg-reprise">
              <span>Même atelier que le {offreReprise.date}{offreReprise.auteur ? ` (${offreReprise.auteur})` : ''}.</span>
              <button type="button" className="btn btn-sm" onClick={onReprendre}>Reprendre ce qui avait été écrit</button>
              <span className="pg-vide">À relire : les personnes ont pu changer.</span>
            </div>
          )}
          {repriseFaite && (
            <div className="pg-reprise">
              <span>Repris du {repriseFaite.date}. À relire : les personnes ont pu changer.</span>
              <button type="button" className="btn btn-sm btn-ghost" onClick={onAnnulerReprise}>Annuler la reprise</button>
            </div>
          )}
          <TexteAdaptatif id="passation-logistique" etiquette="Côté pratique de l’atelier" valeur={logistique} minLignes={4} maxLength={4000} classe={`pg-champ${logistique.trim() ? ' pg-rempli' : ''}`}
            placeholder="Lieu précis, clés ou badge, matériel, déroulé habituel, horaires réels…" onChange={onChange} onBlur={onBlur} />
        </>
      ) : (
        <div className={`pg-texte${logistique.trim() ? '' : ' pg-vide'}`}>{logistique.trim() || 'Rien d’indiqué'}</div>
      )}
    </section>
  );
}
